import React, { useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  filter,
  flatten,
  identity,
  map,
  modulo,
  negate,
  pipe,
  times,
} from "ramda";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotateReverse,
  faRotateLeft,
  faRotateRight,
  faMagnifyingGlassPlus,
  faMagnifyingGlassMinus,
} from "@fortawesome/pro-regular-svg-icons";
import Cropper from "react-cropper";

import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import "cropperjs/dist/cropper.css";

const useStyles = imgSize =>
  makeStyles(theme => ({
    icon: {
      color: "black",
    },

    image: {
      height: imgSize,
      width: imgSize,
      objectFit: "contain",
      borderRadius: "20px",
      boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    },

    label: {
      fontSize: "1.2rem",
    },

    preview: {
      objectFit: "contain",
    },

    title: {
      lineHeight: "1.7rem",
      marginBottom: 14,
      fontWeight: "bold",
    },
  }));

const isMultipleOf60 = x => modulo(x, 60) === 0;
const getMultiplesOf60 = filter(isMultipleOf60);

const withoutNegativeZero = x => (x === 0 ? x : [x, negate(x)]);
const removeNegativeZero = map(withoutNegativeZero);

const formatMark = x => ({ value: x, label: `${x}°` });
const getMarks = () =>
  pipe(
    getMultiplesOf60,
    removeNegativeZero,
    flatten,
    map(formatMark),
  )(times(identity, 181));

const Icon = props => {
  const classes = useStyles()();
  return <FontAwesomeIcon {...props} className={classes.icon} />;
};

const ImageCrop = props => {
  const {
    aspectRatio = null,
    cropper,
    handleInitialized,
    thumbnailUrl,
    currentUrl,
    imgSize = 350,
  } = props;
  const classes = useStyles(imgSize)();

  const [rotate, setRotate] = useState(0);

  const rotateImageLeft = () => {
    cropper.rotate(-90);
    const cropperRotate = cropper.getData().rotate;
    setRotate(cropperRotate < -180 ? cropperRotate - 360 : cropperRotate);
    setRotate(cropper.getData().rotate);
  };

  const rotateImageRight = () => {
    cropper.rotate(90);
    const cropperRotate = cropper.getData().rotate;
    setRotate(cropperRotate > 180 ? cropperRotate - 360 : cropperRotate);
  };

  const rotateImage = value => {
    cropper.rotateTo(value);
    setRotate(cropper.getData().rotate);
  };

  const resetImage = () => {
    cropper.reset();
    setRotate(cropper.getData().rotate);
  };

  const zoomIn = () => cropper.zoom(0.1);
  const zoomOut = () => cropper.zoom(-0.1);

  return (
    <Grid container direction="column">
      <Grid item>
        <Typography className={classes.title}>Edit Image</Typography>
      </Grid>
      <Grid item container justifyContent="flex-end" xs="auto">
        <Cropper
          aspectRatio={aspectRatio}
          autoCropArea={1}
          center={false}
          dragMode="move"
          guides={false}
          onInitialized={handleInitialized}
          src={thumbnailUrl || `${currentUrl}&tc${Date.now()}`}
          style={{ width: imgSize, height: imgSize }}
          viewMode={1}
        />
      </Grid>
      <Grid container direction="column">
        <Grid container item justifyContent="center" alignItems="center">
          <Grid item>
            <Tooltip title="Zoom In" placement="bottom">
              <IconButton
                aria-label="zoom image in"
                onClick={zoomIn}
                size="large"
              >
                <Icon icon={faMagnifyingGlassPlus} />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Zoom Out" placement="bottom">
              <IconButton
                aria-label="zoom image out"
                onClick={zoomOut}
                size="large"
              >
                <Icon icon={faMagnifyingGlassMinus} />
              </IconButton>
            </Tooltip>
          </Grid>

          <Grid item>
            <Tooltip title="Rotate Left" placement="bottom">
              <IconButton
                aria-label="rotate image left"
                onClick={rotateImageLeft}
                size="large"
              >
                <Icon icon={faRotateLeft} />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Rotate Right" placement="bottom">
              <IconButton
                aria-label="rotate image right"
                onClick={rotateImageRight}
                size="large"
              >
                <Icon icon={faRotateRight} />
              </IconButton>
            </Tooltip>
          </Grid>

          <Grid item>
            <Tooltip title="Reset Image" placement="bottom">
              <IconButton
                aria-label="reset picture orientation and size"
                onClick={resetImage}
                size="large"
              >
                <Icon icon={faArrowsRotateReverse} />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid item>
          <Slider
            value={rotate}
            onChange={(_event, value) => rotateImage(value)}
            step={2}
            min={-180}
            max={180}
            marks={getMarks()}
            track={false}
            aria-labelledby="image-rotation"
            classes={{
              markLabel: classes.label,
              valueLabel: classes.label,
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ImageCrop;
